import {
	HTMLAttributes,
	MouseEventHandler,
	ReactNode,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { CalendarWidget } from 'src/UI';
import { createPortal } from 'react-dom';
import { getScrollContainer } from 'src/Utils';
import { Hint } from '@redoc/ui-kit';
import { ReactComponent as CalIcon } from 'src/Themes/assets/calendar.svg';
import { useOutsideClick } from 'src/Hooks';
import ClearBtn from 'src/Components/ClearBtn/ClearBtn';
import cn from 'classnames';
import DateFormater from 'src/Utils/DateFormater';
import InputMask from 'react-input-mask';
import moment from 'moment';
import Regex from 'src/Utils/Regex';
import st from './DateBox.module.scss';

interface IDateBoxProps extends HTMLAttributes<HTMLDivElement> {
	/** Наименование поля */
	name: string;
	/** Значение */
	value: string | Date;
	/** Функция установки значения */
	setValue?: (date: Date | null) => void;
	/** Вариант отображения */
	variant?: 'gray' | 'blue' | 'blueControl';
	/** Разрешить очистку значения */
	allowClear?: boolean;
	/** Только для чтения */
	disabled?: boolean;
	/** С временем */
	withTime?: boolean;
	/** Показывать контрол только для чтения */
	showDisabling?: boolean;
	/** Поле с ошибкой */
	isError?: boolean;
	/** Подсказка */
	hint?: string;
	renderIcon?: boolean;
	renderInput?: boolean;
	minDate?: Date;
	maxDate?: Date;
	isArrow?: boolean;
	renderPlaceHolder?: (date: Date | null, isOpen: boolean) => ReactNode;
}

/** Тип позиции ( top, left ) */
export type PositionType = {
	top: number;
	left: number;
} | null;

/** Возвращает строкое представление значения */
const getStringValue = (value: Date | null): string | undefined => {
	return value ? DateFormater.toShortString(value) : '';
};

/** Возвращает дату */
const getDateValue = (value: Date | string): Date | null => {
	if (!value || value === 'Invalid date') {
		return null;
	}
	if (typeof value === 'object' && !!value?.getTime) {
		return value;
	}
	return moment(value, 'DD.MM.YYYY').toDate();
};

// TODO: в проекте есть два компонента с именем - DateBox
// TODO: логика в функциях дублируется

const DateBox = (props: IDateBoxProps) => {
	const {
		name,
		value,
		setValue,
		disabled = false,
		minDate,
		maxDate,
		isArrow = false,
		showDisabling = true,
		renderPlaceHolder,
		allowClear = false,
		hint = '',
		isError = false,
		variant = 'gray',
		renderIcon = true,
		renderInput = true,
		className,
		...otherProps
	} = props;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [position, setPosition] = useState<PositionType>(null);
	const [tmpValue, setTmpValue] = useState<string>('');

	const modalRef = useOutsideClick<HTMLDivElement>((e) => {
		return !!onOutSideClick && onOutSideClick(e);
	}, 'mouseup');

	useLayoutEffect(() => {
		const wr = wrapperRef.current;

		const calcOffsetModalPosition = () => {
			if (wr) {
				const rect = wr.getBoundingClientRect();
				let top = rect.bottom + window.scrollY + 5;
				let left = rect.left + window.scrollX;
				if (rect.bottom + 340 > window.innerHeight) {
					top = rect.top + window.scrollY - 334 - 35;
				}
				if (rect.left + rect.width > window.innerWidth) {
					left -= 380;
				}
				setPosition({ top, left });
			}
		};

		const onClose = () => {
			return setIsOpened(false);
		};

		if (isOpened) {
			const scrollContainer = getScrollContainer(wr);
			if (scrollContainer) {
				scrollContainer.addEventListener('scroll', onClose);
			}
		}

		calcOffsetModalPosition();

		return () => {
			if (!wr) {
				return;
			}
			const scrollContainer = getScrollContainer(wr);
			if (scrollContainer) {
				scrollContainer.removeEventListener('scroll', onClose);
			}
		};
		// eslint-disable-next-line
	}, [isOpened]);

	useEffect(() => {
		if (!value) {
			setTmpValue('');
		} else if (typeof value === 'string') {
			const _val = getDateValue(value);
			setTmpValue(getStringValue(_val) ?? '');
			// @ts-ignore
		} else if (value.getDate) {
			setTmpValue(getStringValue(value) ?? '');
		}
	}, [value]);

	const onChangeHandler = (newValue: Date | null) => {
		if (setValue) {
			setValue(newValue);
		}
		onSetIsOpen(false);
	};

	const onChangeInputHandler = (e: any) => {
		e.persist();
		const _value: string = e.target.value;
		setTmpValue(_value);

		if (Regex.DateS.test(_value)) {
			const newValue = moment(_value, 'DD.MM.YYYY');
			if (setValue) {
				setValue(newValue.toDate());
			}
		} else if (
			_value.replaceAll('.', '').replaceAll('_', '').length === 0
		) {
			// Компонент маски сам подставляет значение при фокусе на пустом поле,
			// из-за чего срабатывает onChange, будто пользователь что-то ввёл
			if (setValue && value) {
				setValue(null);
			}
		}
	};

	const onClearHandler: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		onChangeHandler(null);
	};

	const onSetIsOpen = (force = false) => {
		if (disabled) {
			return;
		}
		setIsOpened(force);
	};

	const onOutSideClick = (event: MouseEvent) => {
		const isFinded =
			wrapperRef.current &&
			(wrapperRef.current as any).contains(event.target as Node);
		if (!isFinded) {
			onSetIsOpen(false);
		}
	};

	const renderBody = () => {
		const val = getDateValue(value);
		const values = val ? [val] : [];
		return (
			<div ref={modalRef} className={st.modalWrapper}>
				<CalendarWidget
					readOnly={disabled}
					values={values}
					onDateClick={onChangeHandler}
					style={{ position: 'absolute', ...position }}
					minDate={minDate}
					maxDate={maxDate}
				/>
			</div>
		);
	};

	const wrapperClasses = cn(st.rdcDatebox, className, st[variant], {
		[st.notHover]: disabled,
		[st.disabled]: showDisabling && disabled,
		[st.active]: isOpened,
		[st.withArrow]: isArrow,
		[st.invalidate]: isError,
	});

	const val = getDateValue(value);
	return (
		<>
			<Hint messageText={hint} placement="top">
				<div
					ref={wrapperRef}
					className={wrapperClasses}
					onClick={() => {
						return onSetIsOpen(true);
					}}
					{...otherProps}
				>
					{renderIcon && <CalIcon />}
					{renderInput && !renderPlaceHolder && (
						<InputMask
							name={name}
							readOnly={disabled}
							alwaysShowMask={false}
							placeholder="ДД.ММ.ГГГГ"
							value={tmpValue}
							onChange={onChangeInputHandler}
							mask="99.99.9999"
						/>
					)}
					{!!renderPlaceHolder && renderPlaceHolder(val, isOpened)}
					{allowClear && <ClearBtn onClick={onClearHandler} />}
					{isArrow && (
						<svg
							width="33"
							height="32"
							viewBox="0 0 33 32"
							fill="inherit"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.5 13.5L16.5 18.5L21.5 13.5H11.5Z"
								fill="inherit"
							/>
						</svg>
					)}
				</div>
			</Hint>

			{isOpened && createPortal(renderBody(), document.body)}
		</>
	);
};

export default DateBox;
